import React, { useState, useEffect, useMemo } from 'react';
import { Button, Menu, MenuItem, Grid, Skeleton, useTheme, Typography, useMediaQuery } from '@mui/material';
import { DialogContent, DialogTitle, TextField, ListItemText, ListItemIcon } from '@mui/material';
import { FiberManualRecord, ExpandMoreRounded, ChevronRightRounded } from '@mui/icons-material';
import { green, red, grey } from '@mui/material/colors';
import { isEmpty, range, cloneDeep } from 'lodash';
import { RoundedDialog, StandardDialogActions } from '../../../Global/Components';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import axios from 'axios';
import StateManager from '../../../Global/StateManager';

const DefaultStatuses = [
  { _id: 'ok', title: 'OK', color: green[600] },
  { _id: 'lost', title: 'Lost', color: red[600] },
  { _id: 'quarantined', title: 'Quarantined', color: red[600] },
  { _id: 'expired', title: 'Expired', color: red[600] },
  { _id: 'overdue', title: 'Overdue', color: red[600] },
];

export default function StatusButton({ status, onChange, disabled, assetId, additionalStyles }) {
  const theme = useTheme();
  const [statusMenu, setStatusMenu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customStatuses, setCustomStatuses] = useState([]);
  const [newStatusId, setNewStatusId] = useState(false);
  const [commentDialog, setCommentDialog] = useState(false);
  const [locationDialog, setLocationDialog] = useState(false);

  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const Statuses = [...DefaultStatuses, ...customStatuses];
  const backgroundColor = status ? status.color : grey[300];
  const color = theme.palette.getContrastText(backgroundColor);

  const loadStatuses = isEmpty(customStatuses) && Boolean(statusMenu);

  useEffect(() => {
    if (!loadStatuses) return;
    setLoading(true);
    axios
      .get('/asset/status/getStatuses')
      .then((res) => {
        setCustomStatuses(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [loadStatuses]);

  function changeStatus(updatedStatus) {
    const statusesToComment = ['lost', 'quarantined', 'recalled'];

    if (statusesToComment.includes(updatedStatus)) {
      setCommentDialog(true);
      setNewStatusId(updatedStatus);
    } else if (status?._id === 'quarantined') {
      setLocationDialog(true);
      setNewStatusId(updatedStatus);
    } else {
      saveStatus({ newStatusId: updatedStatus });
    }
  }

  function saveStatus({ comment, newStatusId, newLocation }) {
    setNewStatusId(newStatusId);

    const body = {
      comment,
      statusId: newStatusId,
      assetId,
      newLocation,
    };

    axios
      .post('/asset/changeAssetStatus', body)
      .then(({ data }) => {
        onChange(data);
        StateManager.setSuccessAlert('Status has been saved');
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
      });
  }

  return (
    <>
      <Button
        style={{
          textTransform: 'none',
          minWidth: 150,
          backgroundColor,
          color,
          ...additionalStyles,
        }}
        onClick={(event) => {
          event.stopPropagation();
          setStatusMenu(event.currentTarget);
        }}
        disabled={disabled}
      >
        {status?.title || '-'}
      </Button>

      <Menu
        anchorEl={statusMenu}
        open={Boolean(statusMenu)}
        onClose={(event) => {
          event.stopPropagation();
          setStatusMenu(null);
        }}
      >
        {loading ? (
          <Grid container style={{ width: 150 }}>
            {range(5).map((x) => (
              <Grid container key={x} sx={{ mt: 1 }}>
                <Skeleton width={'100%'} />
              </Grid>
            ))}
          </Grid>
        ) : (
          Statuses.map((status) => (
            <MenuItem
              key={status._id}
              onClick={(event) => {
                event.stopPropagation();
                changeStatus(status._id);
                setStatusMenu(null);
              }}
            >
              <ListItemIcon>
                <FiberManualRecord style={{ color: status.color }} />
              </ListItemIcon>
              <ListItemText primary={status.title} />
            </MenuItem>
          ))
        )}
      </Menu>

      <CommentDialog
        open={commentDialog}
        onClose={() => setCommentDialog(false)}
        newStatusId={newStatusId}
        onResult={saveStatus}
      />

      <LocationDialog
        open={locationDialog}
        onClose={() => setLocationDialog(false)}
        newStatusId={newStatusId}
        onResult={saveStatus}
      />
    </>
  );
}

function CommentDialog({ open, onClose, newStatusId, onResult }) {
  const [comment, setComment] = useState('');
  const [quatantineLocations, setQuarantineLocations] = useState([]);
  const [quatantineLocationsLoading, setQuarantineLocationsLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    setComment('');
    setSelectedLocation(null);
  }, [open]);

  const loadQuatantineLocations = newStatusId === 'quarantined' && isEmpty(quatantineLocations);

  useEffect(() => {
    if (!loadQuatantineLocations) return;

    axios
      .get('/asset/location/getQuarantineLocations')
      .then((res) => {
        setQuarantineLocations(res.data);
        setQuarantineLocationsLoading(false);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setQuarantineLocationsLoading(false);
      });
  }, [loadQuatantineLocations]);

  const locationsTree = useMemo(() => {
    const copy = cloneDeep(quatantineLocations.filter((x) => !x.parentId));
    copy.forEach((root) => fillChildNodes(root, quatantineLocations));

    return copy;
  }, [quatantineLocations]); // eslint-disable-line

  function fillChildNodes(root, nodes) {
    delete root.parentId;
    root.children = nodes.filter((item) => item.parentId === root._id);
    root.children.forEach((child) => fillChildNodes(child, nodes));
  }

  function renderTree(node) {
    return (
      <TreeItem
        key={node._id}
        nodeId={node._id}
        label={node.title}
        onClick={() => setSelectedLocation({ _id: node._id, title: node.title })}
      >
        {Array.isArray(node.children) ? node.children.map((node) => renderTree(node)) : null}
      </TreeItem>
    );
  }

  const StatusChangeMessages = {
    lost: 'You about to mark the status as lost',
    quarantined: 'You are about to quarantine the asset',
    recalled: 'You are about to recall the asset',
  };

  const hideDone = !comment || !(newStatusId !== 'quarantined' || selectedLocation);

  function done() {
    onResult({ comment, newStatusId, newLocation: selectedLocation });
    onClose();
  }

  return (
    <RoundedDialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
      <DialogTitle>{StatusChangeMessages[newStatusId]}</DialogTitle>
      <DialogContent>
        {newStatusId === 'quarantined' &&
          (quatantineLocationsLoading ? (
            <Grid container>
              {range(5).map((i) => (
                <Grid container sx={{ my: 1 }} key={i}>
                  <Skeleton width={'100%'} height={32} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container>
              <Grid container sx={{ mb: 1 }}>
                <Typography>Please pick quarantine location</Typography>
              </Grid>

              {isEmpty(locationsTree) ? (
                <Grid container direction={'column'} sx={{ mt: 3 }}>
                  <Typography>No quarantine locations available</Typography>
                  <Typography>Please create one in settings</Typography>
                </Grid>
              ) : (
                <Grid container>
                  <TreeView
                    style={{ minHeight: 50 }}
                    defaultCollapseIcon={<ExpandMoreRounded />}
                    defaultExpandIcon={<ChevronRightRounded />}
                  >
                    {locationsTree.map((node) => renderTree(node))}
                  </TreeView>
                </Grid>
              )}
            </Grid>
          ))}
        <Grid container sx={{ mt: 2 }}>
          <TextField
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            label="Please provide a comment"
            required
            multiline
            minRows={5}
          />
        </Grid>
      </DialogContent>
      <StandardDialogActions onClose={onClose} hideDone={hideDone} onDone={done} />
    </RoundedDialog>
  );
}

function LocationDialog({ open, onClose, newStatusId, onResult }) {
  const [quatantineLocations, setLocations] = useState([]);
  const [quatantineLocationsLoading, setLocationsLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    setSelectedLocation(null);
  }, [open]);

  const loadQuatantineLocations = open && isEmpty(quatantineLocations);

  useEffect(() => {
    if (!loadQuatantineLocations) return;

    axios
      .get('/asset/location/getLocations')
      .then((res) => {
        setLocations(res.data);
        setLocationsLoading(false);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setLocationsLoading(false);
      });
  }, [loadQuatantineLocations]);

  const locationsTree = useMemo(() => {
    const copy = cloneDeep(quatantineLocations);
    copy.forEach((root) => fillChildNodes(root, quatantineLocations));

    return copy;
  }, [quatantineLocations]); // eslint-disable-line

  function fillChildNodes(root, nodes) {
    delete root.parentId;
    root.children = nodes.filter((item) => item.parentId === root._id);
    root.children.forEach((child) => fillChildNodes(child, nodes));
  }

  function renderTree(node) {
    return (
      <TreeItem
        key={node._id}
        nodeId={node._id}
        label={node.title}
        onClick={() => setSelectedLocation({ _id: node._id, title: node.title })}
      >
        {Array.isArray(node.children) ? node.children.map((node) => renderTree(node)) : null}
      </TreeItem>
    );
  }

  const hideDone = !selectedLocation;

  function done() {
    onResult({ newStatusId, newLocation: selectedLocation });
    onClose();
  }

  return (
    <RoundedDialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
      <DialogTitle>You want to bring the asset back from quarantine</DialogTitle>
      <DialogContent>
        {quatantineLocationsLoading ? (
          <Grid container>
            {range(5).map((i) => (
              <Grid container sx={{ my: 1 }} key={i}>
                <Skeleton width={'100%'} height={32} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container>
            <Grid container sx={{ mb: 1 }}>
              <Typography>Please pick normal location</Typography>
            </Grid>

            <Grid container>
              <TreeView
                style={{ minHeight: 50 }}
                defaultCollapseIcon={<ExpandMoreRounded />}
                defaultExpandIcon={<ChevronRightRounded />}
              >
                {locationsTree.map((node) => renderTree(node))}
              </TreeView>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <StandardDialogActions onClose={onClose} hideDone={hideDone} onDone={done} />
    </RoundedDialog>
  );
}
